import {
  BaseInputProps,
  DateInput,
  Dropdown,
  FormTemplate,
  IfFormData,
  NumberInput,
  RadioButtonsGroup,
  RelativeRow,
  TextArea,
  TextInput,
  useListContextRequired,
  useFormContextRequired,
  SimpleBlock, AfterSubmitFunc, CheckboxInput
} from 'ui-builder';
import { ProjectDetailsFieldType, RelativeDateOffset, useAiLauncherService } from 'nekst-api';
import React from 'react';
import { EntryType, VerifyDataEntry } from './types';

import { EmptyCell } from '../../../../shared/web/uitheme/form/RelativeRow';
import { BusinessDaysToggleWrapper } from 'feature-toggle';
import { Separator } from 'features/nekst-widgets';

function RelativeDateOffsetInput(props: BaseInputProps<RelativeDateOffset> & { parentFieldId?: number }) {
  const listContext = useListContextRequired<VerifyDataEntry>();

  const formContext = useFormContextRequired<{
    value: RelativeDateOffset
  }>();

  const dependsOnFieldId = formContext.data!.value?.dependsOnFieldId ?? props.parentFieldId;

  const fieldName = listContext.data!
    .find((item) => item.detailsField?.id === dependsOnFieldId)?.name;

  return (
    <SimpleBlock>
      Set Up offset from {fieldName}:<br /><br />
      <RelativeRow weights={[2, 3, 1, 6]} useFlex centerV>
        <NumberInput source="value.days" label="Days" />
        <Dropdown
          options={[
            {
              label: 'Before',
              value: 'before'
            },
            {
              label: 'After',
              value: 'after'
            }
          ]}
          source="value.when"
          isClearable={false}
        />
        <EmptyCell />
        <BusinessDaysToggleWrapper>
          <CheckboxInput source="value.useBusinessDaysCalculation" label="Business Days" />
        </BusinessDaysToggleWrapper>
      </RelativeRow>
    </SimpleBlock>

  );
}

type CombinedDateMode = 'relative' | 'absolute';

type CombinedDateFormData = {
  mode: CombinedDateMode,
  relativeValue: RelativeDateOffset,
  absoluteValue: Date | null
}

function CombinedRelativeDateOffsetInput(props: { parentFieldId: number }) {
  const listContext = useListContextRequired<VerifyDataEntry>();

  const formContext = useFormContextRequired<CombinedDateFormData>();

  const dependsOnFieldId = formContext.data!.relativeValue?.dependsOnFieldId ?? props.parentFieldId;

  const fieldName = listContext.data!
    .find((item) => item.detailsField?.id === dependsOnFieldId)?.name;

  return (
    <SimpleBlock>
      Set Up offset from {fieldName}:<br /><br />
      <RelativeRow weights={[2, 3, 1, 6]} useFlex centerV>
        <NumberInput source="relativeValue.days" label="Days" />
        <Dropdown
          options={[
            {
              label: 'Before',
              value: 'before'
            },
            {
              label: 'After',
              value: 'after'
            }
          ]}
          source="relativeValue.when"
          isClearable={false}
        />
        <EmptyCell />
        <BusinessDaysToggleWrapper>
          <CheckboxInput source="relativeValue.useBusinessDaysCalculation" label="Business Days" />
        </BusinessDaysToggleWrapper>
      </RelativeRow>
    </SimpleBlock>
  );
}

function CombinedDateForm(props: {
  data: VerifyDataEntry,
  parentFieldId: number,
}) {
  const parentFieldName = useListContextRequired<VerifyDataEntry>().data!
    .find((item) => item.detailsField?.id === props.parentFieldId)?.name ?? 'parent field';

  return (
    <SimpleBlock>
      <RadioButtonsGroup<CombinedDateMode>
        source="mode"
        label="Choose how to set this date:"
        options={[
          {
            label: `Relative to ${parentFieldName}`,
            value: 'relative',
          },
          {
            label: 'Specific Date',
            value: 'absolute',
          }
        ]}
      />
      <Separator />
      <IfFormData<CombinedDateFormData>
        conditionFunc={(data) => data.mode === 'relative'}
      >
        <CombinedRelativeDateOffsetInput parentFieldId={props.parentFieldId} />
      </IfFormData>
      <IfFormData<CombinedDateFormData>
        conditionFunc={(data) => data.mode === 'absolute'}
      >
        <DateInput source="absoluteValue" label={props.data.name} />
      </IfFormData>
    </SimpleBlock>
  );
}

function shouldShowCombinedDateWidget(data: VerifyDataEntry): boolean {
  return data.detailsField?.fieldType === ProjectDetailsFieldType.DATE
    && data.detailsField?.parentFieldId != null
    && (data.relatedTask == null || data.relatedTask?.parentTaskRelation == null);
}

function SimpleParseValueForm(props: {
  projectId: number
  data: VerifyDataEntry,
  afterSubmitFunc?: AfterSubmitFunc,
}) {

  const listContext = useListContextRequired<VerifyDataEntry>();

  const aiLauncherService = useAiLauncherService();

  const projectId = props.projectId;

  const updateData = async (value: string | number | Date | RelativeDateOffset | null | undefined) => {
    if (props.data.type === EntryType.NAME) {
      return aiLauncherService.updateName(projectId, value as string);
    } else {

      const detailsField = props.data.detailsField!;
      if (detailsField.fieldType === ProjectDetailsFieldType.PRICE) {
        return aiLauncherService.updateNumberFieldValue(projectId, detailsField.id, value as number | undefined);
      } else if (detailsField.fieldType === ProjectDetailsFieldType.DATE) {
        if (props.data.parsedValue?.type === 'relative_date_offset') {
          const rdo = value as RelativeDateOffset | undefined;

          if (rdo?.days !== undefined) {
            return aiLauncherService.updateRelativeDateOffsetValue(projectId, detailsField.id, value as RelativeDateOffset | undefined);
          } else {
            return aiLauncherService.updateRelativeDateOffsetValue(projectId, detailsField.id, undefined);
          }

        } else {
          return aiLauncherService.updateDateFieldValue(projectId, detailsField.id, value as Date | undefined);
        }
      } else {
        return aiLauncherService.updateStringFieldValue(projectId, detailsField.id, value as string | undefined);
      }
    }
  };

  let Widget;

  if (props.data.type === EntryType.NAME) {
    Widget = TextInput;
  } else {
    const detailsField = props.data.detailsField!;

    if (detailsField.fieldType === ProjectDetailsFieldType.PRICE) {
      Widget = NumberInput;
    } else if (detailsField.fieldType === ProjectDetailsFieldType.LONG_TEXT) {
      Widget = TextArea;
    } else if (detailsField.fieldType === ProjectDetailsFieldType.DATE) {
      if (props.data.parsedValue?.type === 'relative_date_offset') {
        Widget = RelativeDateOffsetInput;
      } else {
        Widget = DateInput;
      }
    } else {
      Widget = TextInput;
    }
  }

  return (
    <FormTemplate<{ value: string | number | Date | RelativeDateOffset | null | undefined }>
      title={`Update "${props.data.name}" value`}
      submitFormFunc={async (request) => {
        await updateData(request.value);

        const listData = listContext.data.map((item) => {
          if (item.type === props.data.type && item.detailsField?.id === props.data.detailsField?.id) {
            return {
              ...item,
              parsedValue: {
                ...item.parsedValue,
                value: request.value
              }
            } as VerifyDataEntry;
          } else {
            return item;
          }
        });

        listContext._setData(listData);

        return request;
      }}
      initialData={{
        value: props.data.parsedValue?.value
      }}
      afterSubmitFunc={props.afterSubmitFunc}
    >
      <Widget source="value" label={props.data.name} />
    </FormTemplate>
  );
}

function CombinedParseValueForm(props: {
  projectId: number
  data: VerifyDataEntry,
  afterSubmitFunc?: AfterSubmitFunc,
}) {

  const aiLauncherService = useAiLauncherService();
  const projectId = props.projectId;


  const listContext = useListContextRequired<VerifyDataEntry>();

  const parentFieldId = props.data.detailsField!.parentFieldId!;
  const isCurrentlyRelative = props.data.parsedValue?.type === 'relative_date_offset';
  const currentRelativeValue = isCurrentlyRelative
    ? props.data.parsedValue?.value as RelativeDateOffset
    : {
      days: 0,
      when: 'after',
      dependsOnFieldId: parentFieldId,
      useBusinessDaysCalculation: false
    } as RelativeDateOffset;
  const currentAbsoluteValue = !isCurrentlyRelative && props.data.parsedValue?.type === 'date'
    ? props.data.parsedValue?.value as Date
    : null;

  return (
    <FormTemplate<CombinedDateFormData>
      title={`Update "${props.data.name}" value`}
      submitFormFunc={async (request) => {
        const detailsField = props.data.detailsField!;

        if (request.mode === 'absolute') {
          await aiLauncherService.updateDateFieldValue(projectId, detailsField.id, request.absoluteValue ?? undefined);
        } else {
          const rdo = request.relativeValue;
          if (rdo?.days !== undefined) {
            await aiLauncherService.updateRelativeDateOffsetValue(projectId, detailsField.id, rdo);
          } else {
            await aiLauncherService.updateRelativeDateOffsetValue(projectId, detailsField.id, undefined);
          }
        }

        const newType = request.mode === 'absolute' ? 'date' : 'relative_date_offset';
        const newValue = request.mode === 'absolute' ? request.absoluteValue : request.relativeValue;

        const listData = listContext.data.map((item) => {
          if (item.type === props.data.type && item.detailsField?.id === props.data.detailsField?.id) {
            return {
              ...item,
              parsedValue: {
                ...item.parsedValue,
                type: newType,
                value: newValue
              }
            } as VerifyDataEntry;
          } else {
            return item;
          }
        });

        listContext._setData(listData);

        return request;
      }}
      initialData={{
        mode: isCurrentlyRelative ? 'relative' : 'absolute',
        relativeValue: currentRelativeValue,
        absoluteValue: currentAbsoluteValue,
      }}
      afterSubmitFunc={props.afterSubmitFunc}
    >
      <CombinedDateForm data={props.data} parentFieldId={parentFieldId} />
    </FormTemplate>
  );
}



export function UpdateParseValueForm(props: {
  projectId: number
  data: VerifyDataEntry,
  afterSubmitFunc?: AfterSubmitFunc,
}) {
  const showCombinedWidget = shouldShowCombinedDateWidget(props.data);

  if (showCombinedWidget) {
    return (
      <CombinedParseValueForm projectId={props.projectId} data={props.data} afterSubmitFunc={props.afterSubmitFunc} />
    )
  } else {
    return (
      <SimpleParseValueForm projectId={props.projectId} data={props.data} afterSubmitFunc={props.afterSubmitFunc} />
    )
  }


}
